import { useState } from "react";
import type { CoachingPlaylist, Match } from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { useFeedbackReview } from "./hooks/useFeedbackReview";
import { usePlaylistCover } from "./hooks/usePlaylistCover";
import { useThumbnail } from "@/features/coach-review/hooks/useThumbnail";

/**
 * `/feedback` Playlists tab (M7) — the viewer's review playlists.
 * Ports legacy `renderFeedbackPlaylists` (`js/coaching/feedback.js`).
 *
 * Each card: a cover thumbnail (the first item's note thumbnail),
 * a "Review Session" kicker, the item count, the description, a
 * Reviewed/New pill, and two actions — "Play session" (opens the
 * M5 `<FeedbackPlayerModal>` in `playlist` mode) and "Mark
 * reviewed".
 *
 * The playlist's `items[]` are embedded notes, already
 * viewer-filtered + scrubbed server-side. `FeedbackPlayerModal`
 * resolves each item's own match in playlist mode, but still
 * needs a fallback `match` prop — the first item's match.
 */

export type FeedbackPlaylistsTabProps = {
  playlists: CoachingPlaylist[];
  /** playlist_ids the viewer has marked reviewed. */
  reviewedPlaylistIds: Set<number>;
};

/** Fallback `match` for the M5 modal — the playlist's first
 * item's match. Playlist mode resolves each item's match itself,
 * but the prop is still required (see `FeedbackPlayerModalProps`). */
function firstItemMatch(
  playlist: CoachingPlaylist,
): Pick<Match, "id" | "team_id"> {
  const first = playlist.items?.[0];
  return {
    id: first?.match_id ?? "",
    team_id: first?.team_id ?? playlist.team_id,
  };
}

export function FeedbackPlaylistsTab({
  playlists,
  reviewedPlaylistIds,
}: FeedbackPlaylistsTabProps) {
  const [openPlaylist, setOpenPlaylist] = useState<CoachingPlaylist | null>(
    null,
  );
  const review = useFeedbackReview();

  if (playlists.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-playlists-empty"
      >
        No review playlists have been shared with you yet.
      </p>
    );
  }

  return (
    <>
      <ul
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
        data-testid="feedback-playlists-list"
      >
        {playlists.map((playlist) => (
          <PlaylistCard
            key={playlist.id}
            playlist={playlist}
            reviewed={reviewedPlaylistIds.has(playlist.id)}
            reviewPending={review.isPending}
            onPlay={() => setOpenPlaylist(playlist)}
            onMarkReviewed={() =>
              review.markReviewed({ playlist_id: playlist.id })
            }
          />
        ))}
      </ul>

      {/* The M5 focused player — playlist mode. */}
      {openPlaylist ? (
        <FeedbackPlayerModal
          target={{ kind: "playlist", playlist: openPlaylist }}
          match={firstItemMatch(openPlaylist)}
          onClose={() => setOpenPlaylist(null)}
        />
      ) : null}
    </>
  );
}

function PlaylistCard({
  playlist,
  reviewed,
  reviewPending,
  onPlay,
  onMarkReviewed,
}: {
  playlist: CoachingPlaylist;
  reviewed: boolean;
  reviewPending: boolean;
  onPlay: () => void;
  onMarkReviewed: () => void;
}) {
  const title = playlist.title?.trim() || "(untitled playlist)";
  const itemCount =
    playlist.items?.length ?? playlist.note_ids?.length ?? 0;
  // Cover = the FIRST item whose standalone thumbnail the viewer
  // can actually load — walked via `usePlaylistCover`. Legacy
  // parity (`_resolveFeedbackPlaylistCover`): a playlist whose
  // first item has no thumb (or whose first item is a private
  // note that 403s the standalone endpoint) still shows the next
  // playable item's cover. Returns `null` until resolved / on
  // total failure ⇒ placeholder.
  const coverNoteId = usePlaylistCover(playlist);

  return (
    <li
      className="flex flex-col rounded-lg border border-rule bg-bg-card overflow-hidden"
      data-testid="feedback-playlist-card"
      data-playlist-id={playlist.id}
    >
      {/* Cover thumbnail — full-width at top of card */}
      <div className="relative h-36 w-full bg-gradient-to-br from-canvas to-surface overflow-hidden">
        {coverNoteId ? (
          <PlaylistCoverImage noteId={coverNoteId} />
        ) : null}
      </div>
      <div className="flex flex-col gap-1 p-3 flex-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Review Session
        </span>
        <div className="font-display text-ink truncate">
          {title}
        </div>
        <div className="text-ink-muted text-xs font-mono truncate">
          {itemCount} {itemCount === 1 ? "clip" : "clips"} ·{" "}
          {reviewed ? "Reviewed" : "New"}
        </div>
        {playlist.description?.trim() ? (
          <div className="text-ink-muted text-xs line-clamp-2">
            {playlist.description.trim()}
          </div>
        ) : null}
        <div className="flex items-center gap-1 mt-auto pt-2">
          <button
            type="button"
            onClick={onPlay}
            data-testid="feedback-playlist-play"
            className="bg-accent hover:bg-accent-hover text-white rounded px-3 py-1.5 text-xs font-display uppercase tracking-wide transition-colors"
          >
            ▶ Play session
          </button>
          <button
            type="button"
            onClick={onMarkReviewed}
            disabled={reviewPending}
            data-testid="feedback-playlist-mark-reviewed"
            className="border border-rule text-ink-muted hover:text-ink hover:bg-surface disabled:opacity-50 rounded px-3 py-1.5 text-xs font-display uppercase tracking-wide transition-colors"
          >
            {reviewed ? "Reviewed ✓" : "Mark reviewed"}
          </button>
        </div>
      </div>
    </li>
  );
}

function PlaylistCoverImage({ noteId }: { noteId: number }) {
  const url = useThumbnail("note", noteId);
  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 h-full w-full object-cover"
    />
  );
}
